import AddIcon from "@mui/icons-material/Add";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import MovieCreationOutlinedIcon from "@mui/icons-material/MovieCreationOutlined";
import PermIdentityOutlinedIcon from "@mui/icons-material/PermIdentityOutlined";
import React, { forwardRef, useEffect, useMemo } from "react";
import { Actor } from "../../models/Actor";
import {
  getActorAvatarCssColor,
  getActorAvatarInitial,
  getActorAvatarSoftStyle,
} from "../../helpers/actorAvatar";
import "./InteractionPointPreview.css";
import L from "leaflet";
import { Marker, Popup, Tooltip, useMap } from "react-leaflet";
import ReactDOMServer from "react-dom/server";
import classNames from "classnames";
import PiBoxIcon from "../../icons/PiBoxIcon";
import { PropMetadata } from "../../dto/PropMetadata";
import { TranslatedPropDTO } from "../../dto/TranslatedPropDTO";
import { UsedProp } from "../../models/UsedProp";
import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import ShapeLineOutlinedIcon from "@mui/icons-material/ShapeLineOutlined";
import CheckIcon from "@mui/icons-material/Check";
import type { RgbaColor } from "../../app/slices/EnvironmentSlice";
import {
  TAKE_LOCATION_COLLAPSE_EVENT,
  TAKE_LOCATION_COLLAPSE_MS,
  TAKE_LOCATION_EXPAND_MS,
} from "./takeLocationAnimation";
export interface InteractionPointPreviewProps {
  strategy: InteractionPointPreviewStrategy;
  x: number;
  y: number;
  /** Normalized parent location to fly out from when this marker appears. */
  animateFrom?: { id: string; x: number; y: number };
  // onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onClick?: (event: MouseEvent) => void;
  actor?: Actor;
  prop?: UsedProp;
  disabled?: boolean;
  popoverContent?: React.ReactNode;
  markerBorderColor?: RgbaColor;
  markerBackgroundColor?: RgbaColor;
  markerIconColor?: RgbaColor;
  directionDegrees?: number;
  directionColor?: RgbaColor;
  showCheckBadge?: boolean;
  /** Small counter in the marker's top-right corner (e.g. folded position count). */
  badgeContent?: React.ReactNode;
  badgeClassName?: string;
  badgeColor?: RgbaColor;
  /** Content hanging off the marker's bottom-left corner (e.g. avatar stack). */
  footerContent?: React.ReactNode;
  /** Hover tooltip below the marker. Not shown while disabled. */
  tooltipContent?: React.ReactNode;
  /** Where the hover tooltip opens; "top" suits larger content such as a camera preview. */
  tooltipDirection?: "top" | "bottom";
  /** Replaces the default dark label style of the hover tooltip. */
  tooltipClassName?: string;
  /** Extra class on the marker's Leaflet icon element, e.g. for map-wide styling. */
  iconClassName?: string;
  /** Native marker label, also available when navigating markers by keyboard. */
  title?: string;
  /** Raises the marker above its neighbours regardless of map position. */
  zIndexOffset?: number;
}

export interface InteractionPointPreviewStrategy {
  getView: (imgUrl?: string, actor?: Actor) => React.ReactNode;
  getStyle: (xProc: string, yProc: string, disabled?: boolean) => {};
  getClassnames: (disabled?: boolean) => string;
}

export const InteractionPointStrategy: InteractionPointPreviewStrategy = {
  getView() {
    return (
      <>
        <AddIcon fontSize="inherit" color="inherit" />
      </>
    );
  },
  getStyle(xProc: string, yProc: string) {
    return {
      "--marker-x": xProc,
      "--marker-y": yProc,
      "--marker-background-color": "var(--pi-key-blue)",
      "--marker-border-color": "var(--pi-key-blue)",
      "--marker-font-color": "white",
    };
  },

  getClassnames() {
    return "bg-creator-primary text-white";
  },
};

export const NpcPointStrategy: InteractionPointPreviewStrategy = {
  getView() {
    return (
      <>
        <PermIdentityOutlinedIcon fontSize="inherit" color="inherit" />
      </>
    );
  },
  getStyle(xProc: string, yProc: string, disabled?: boolean) {
    return {
      "--marker-x": xProc,
      "--marker-y": yProc,
      "--marker-background-color": "var(--pi-surface)",
      "--marker-border-color": "var(--pi-disabled)",
      "--marker-font-color": "var(--pi-text-color-1)",
      opacity: disabled ? "0.5" : "1",
    };
  },
  getClassnames(disabled?: boolean) {
    return classNames({
      "bg-creator-surface b-creator-primary border-2 border-solid border-creator-primary-disabled text-creator-text":
        true,
      "opacity-50": disabled,
    });
  },
};

export const SlatePointStrategy: InteractionPointPreviewStrategy = {
  getView() {
    return (
      <>
        <MovieCreationOutlinedIcon fontSize="inherit" color="inherit" />
      </>
    );
  },
  getStyle(xProc: string, yProc: string) {
    return {
      "--marker-x": xProc,
      "--marker-y": yProc,
      "--marker-background-color": "var(--pi-key-blue)",
      "--marker-border-color": "var(--pi-key-blue)",
      "--marker-font-color": "white",
    };
  },
  getClassnames() {
    return "bg-creator-primary text-white";
  },
};

export const ActorPointStrategy: InteractionPointPreviewStrategy = {
  getView(_imgUrl?: string, actor?: Actor) {
    return (
      <div
        className="flex items-center justify-center rounded-full font-semibold"
        // Markers stay round; they take the soft avatar colours.
        style={{ width: "32px", height: "32px", fontSize: "14px", ...getActorAvatarSoftStyle(getActorAvatarCssColor(actor)) }}
      >
        {actor?.id === "player" ? "P" : getActorAvatarInitial(actor?.workingName)}
      </div>
    );
  },
  getStyle(xProc: string, yProc: string, disabled?: boolean) {
    return {
      "--marker-x": xProc,
      "--marker-y": yProc,
      "--marker-background-color": "var(--pi-game-blue)",
      "--marker-border-color": "var(--pi-game-blue)",
      "--marker-font-color": "white",
      opacity: disabled ? "0.5" : "1",
    };
  },
  getClassnames(disabled?: boolean) {
    return classNames({
      "bg-interaction-primary border-2 border-solid border-transparent": true,
      "opacity-50": disabled,
    });
  },
};

export const PlayerPointStrategy: InteractionPointPreviewStrategy = {
  getView(imgUrl?: string) {
    return (
      <>
        <div className="interactionpointpreview-node-player">P</div>
      </>
    );
  },
  getStyle(xProc: string, yProc: string, disabled?: boolean) {
    return {
      "--marker-x": xProc,
      "--marker-y": yProc,
      "--marker-background-color": "var(--pi-key-blue)",
      "--marker-border-color": "var(--pi-key-blue)",
      "--marker-font-color": "white",
      opacity: disabled ? "0.5" : "1",
    };
  },
  getClassnames(disabled?: boolean) {
    return classNames({
      "text-white bg-creator-primary": true,
      "opacity-50": disabled,
    });
  },
};

export const CAMERA_MARKER_CLASS = "interactionpointpreview-camera-marker";
/** Cameras draw over every other marker, take location pins included, wherever they overlap. */
export const CAMERA_MARKER_Z_INDEX_OFFSET = 100000;

export const CameraPointStrategy: InteractionPointPreviewStrategy = {
  getView: function (imgUrl?: string | undefined): React.ReactNode {
    return <CameraAltOutlinedIcon fontSize="inherit" color="inherit" />;
  },
  getStyle: function (xProc: string, yProc: string): {} {
    return {
      "--marker-x": xProc,
      "--marker-y": yProc,
      "--marker-background-color": "var(--pi-surface)",
      "--marker-border-color": "var(--pi-disabled)",
      "--marker-font-color": "var(--pi-text-color-1)",
      fontSize: "24px",
    };
  },
  getClassnames() {
    return "bg-creator-surface text-creator-text border-2 border-solid border-creator-primary-disabled";
  },
};

export const SelectedCameraPointStrategy: InteractionPointPreviewStrategy = {
  getView: function (imgUrl?: string | undefined): React.ReactNode {
    return <CameraAltOutlinedIcon fontSize="inherit" color="inherit" />;
  },
  getStyle: function (xProc: string, yProc: string): {} {
    return {
      "--marker-x": xProc,
      "--marker-y": yProc,
      "--marker-background-color": "var(--pi-key-blue)",
      "--marker-border-color": "var(--pi-key-blue)",
      "--marker-font-color": "white",
      fontSize: "24px",
    };
  },
  getClassnames() {
    return "bg-creator-primary border-2 border-solid border-creator-primary text-white";
  },
};

export const EmptyPropPointStrategy: InteractionPointPreviewStrategy = {
  getView: function (imgUrl?: string | undefined): React.ReactNode {
    return <PiBoxIcon className={"fill-current"} width={24} height={24} />;
  },
  getStyle: function (
    xProc: string,
    yProc: string,
    disabled?: boolean | undefined
  ): {} {
    throw new Error("Function not implemented.");
  },
  getClassnames: function (disabled?: boolean | undefined): string {
    return classNames({
      "bg-creator-surface b-creator-primary border-2 border-solid border-creator-primary-disabled text-creator-text":
        true,
      "opacity-50": disabled,
    });
  },
};

export const PropPointStrategy: InteractionPointPreviewStrategy = {
  getView: function (imgUrl?: string | undefined): React.ReactNode {
    if (!imgUrl)
      return (
        <div className="text-[16px]">
          <ViewInArOutlinedIcon fontSize="inherit" color="inherit" />
        </div>
      );
    return (
      <img
        src={imgUrl}
        style={{ width: "32px", height: "32px", borderRadius: "100%" }}
        alt="prop"
        className={"object-contain"}
      />
    );
  },
  getStyle: function (
    xProc: string,
    yProc: string,
    disabled?: boolean | undefined
  ): {} {
    throw new Error("Function not implemented.");
  },
  getClassnames: function (disabled?: boolean | undefined): string {
    return classNames({
      "bg-creator-surface border-2 border-solid border-creator-primary text-creator-primary":
        true,
      "opacity-50": disabled,
    });
  },
};

export const SituationPointStrategy: InteractionPointPreviewStrategy = {
  getView: function (imgUrl?: string | undefined): React.ReactNode {
    if (!imgUrl)
      return (
        <div className="text-[20px] flex justify-center items-center">
          <ShapeLineOutlinedIcon fontSize="inherit" color="inherit" />
        </div>
      );
    return (
      <img
        src={imgUrl}
        style={{ width: "32px", height: "32px", borderRadius: "100%" }}
        alt="prop"
        className={"object-contain"}
      />
    );
  },
  getStyle: function (
    xProc: string,
    yProc: string,
    disabled?: boolean | undefined
  ): {} {
    throw new Error("Function not implemented.");
  },
  getClassnames: function (disabled?: boolean | undefined): string {
    return classNames({
      "bg-creator-secondary border-2 border-solid border-creator-secondary":
        true,
      "opacity-50": disabled,
    });
  },
};

export const PlacedSituationPointStrategy: InteractionPointPreviewStrategy = {
  getView: function (imgUrl?: string | undefined): React.ReactNode {
    if (!imgUrl)
      return (
        <div className="text-[20px] flex justify-center items-center">
          <ShapeLineOutlinedIcon fontSize="inherit" color="inherit" />
        </div>
      );
    return (
      <img
        src={imgUrl}
        style={{ width: "33px", height: "33px", borderRadius: "100%" }}
        alt="prop"
        className={"object-contain"}
      />
    );
  },
  getStyle: function (
    xProc: string,
    yProc: string,
    disabled?: boolean | undefined
  ): {} {
    throw new Error("Function not implemented.");
  },
  getClassnames: function (disabled?: boolean | undefined): string {
    return classNames({
      "bg-creator-surface border-2 border-solid border-creator-secondary": true,
      "opacity-50": disabled,
    });
  },
};

export const CurrentTakeSituationPointStrategy: InteractionPointPreviewStrategy =
  {
    getView: function (imgUrl?: string | undefined): React.ReactNode {
      if (!imgUrl)
        return (
          <div className="text-[20px] flex justify-center items-center">
            <MovieCreationOutlinedIcon fontSize="inherit" color="inherit" />
          </div>
        );
      return (
        <img
          src={imgUrl}
          style={{ width: "33px", height: "33px", borderRadius: "100%" }}
          alt="prop"
          className={"object-contain"}
        />
      );
    },
    getStyle: function (
      xProc: string,
      yProc: string,
      disabled?: boolean | undefined
    ): {} {
      throw new Error("Function not implemented.");
    },
    getClassnames: function (disabled?: boolean | undefined): string {
      return classNames({
        "bg-creator-secondary border-2 border-solid border-creator-secondary":
          true,
        "opacity-50": disabled,
      });
    },
  };

/** Unfolded take location: outlined slate in the take colour. */
export const TakeLocationPinPointStrategy: InteractionPointPreviewStrategy = {
  getView: function (): React.ReactNode {
    return <MovieCreationOutlinedIcon fontSize="inherit" color="inherit" />;
  },
  getStyle: function (xProc: string, yProc: string, disabled?: boolean): {} {
    return {
      "--marker-x": xProc,
      "--marker-y": yProc,
      "--marker-background-color": "var(--pi-surface)",
      "--marker-border-color": "var(--pi-disabled)",
      "--marker-font-color": "var(--pi-text-color-1)",
      opacity: disabled ? "0.5" : "1",
    };
  },
  getClassnames: function (disabled?: boolean): string {
    return classNames({
      "bg-white border-2 border-solid border-creator-primary text-creator-primary":
        true,
      "opacity-50": disabled,
    });
  },
};

/** Collapsed take location uses the same slate, with a position count badge. */
export const CollapsedLocationPointStrategy: InteractionPointPreviewStrategy = {
  ...TakeLocationPinPointStrategy,
};

const InteractionPointPreview = forwardRef<
  HTMLDivElement,
  InteractionPointPreviewProps
>((props, ref) => {
  const {
    strategy,
    x,
    y,
    onClick,
    actor,
    prop,
    disabled,
    popoverContent,
    markerBorderColor,
    markerBackgroundColor,
    markerIconColor,
    directionDegrees,
    directionColor,
    showCheckBadge,
    badgeContent,
    footerContent,
    tooltipContent,
    tooltipDirection = "bottom",
    tooltipClassName = "interactionpointpreview-tooltip",
    zIndexOffset,
    iconClassName,
  } = {
    ...props,
  };
  const popupRef = React.useRef<L.Popup>(null);
  const markerRef = React.useRef<L.Marker>(null);
  const map = useMap();
  // Project before the first render so Leaflet never sees normalized coordinates
  // as latitude/longitude and moves the marker from the middle of the map.
  const latLang = useMemo(
    () => map.unproject([512 * x, 512 * y], 1),
    [map, x, y]
  );
  const originX = props.animateFrom?.x;
  const originY = props.animateFrom?.y;
  const originId = props.animateFrom?.id;
  // React Leaflet attaches the marker in its effect, before this parent effect.
  useEffect(() => {
    if (
      originX === undefined ||
      originY === undefined ||
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches
    ) return;
    const element = markerRef.current?.getElement()?.querySelector<HTMLElement>(
      ".interactionpointpreview-flight"
    );
    if (!element?.animate) return;

    const origin = map.latLngToLayerPoint(
      map.unproject([512 * originX, 512 * originY], 1)
    );
    const destination = map.latLngToLayerPoint(latLang);
    const offset = `translate(${origin.x - destination.x}px, ${origin.y - destination.y}px)`;
    let animation = element.animate(
      [
        {
          transform: offset,
        },
        { transform: "translate(0, 0)" },
      ],
      { duration: TAKE_LOCATION_EXPAND_MS, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" }
    );
    const collapse = (event: L.LeafletEvent) => {
      const target = event as L.LeafletEvent & { locationId: string };
      if (target.locationId !== originId) return;
      const currentElement = markerRef.current?.getElement()?.querySelector<HTMLElement>(
        ".interactionpointpreview-flight"
      );
      if (!currentElement?.animate) return;
      // Reverse from the visible position, even if expansion is still running.
      const from = getComputedStyle(currentElement).transform;
      animation.cancel();
      const parentPoint = map.latLngToLayerPoint(
        map.unproject([512 * originX, 512 * originY], 1)
      );
      const markerPoint = map.latLngToLayerPoint(latLang);
      animation = currentElement.animate(
        [
          { transform: from },
          { transform: `translate(${parentPoint.x - markerPoint.x}px, ${parentPoint.y - markerPoint.y}px)` },
        ],
        {
          duration: TAKE_LOCATION_COLLAPSE_MS,
          easing: "cubic-bezier(0.4, 0, 1, 1)",
          fill: "forwards",
        }
      );
    };
    const cancel = () => animation.cancel();
    map.on("zoomstart", cancel);
    map.on(TAKE_LOCATION_COLLAPSE_EVENT, collapse);
    return () => {
      cancel();
      map.off("zoomstart", cancel);
      map.off(TAKE_LOCATION_COLLAPSE_EVENT, collapse);
    };
  }, [map, latLang, originX, originY, originId]);

  const imageUrl = useMemo(() => {
    if (prop) return prop.propContent.downloadUri;

    return undefined;
  }, [prop]);

  const toCssRgba = (color: RgbaColor) => {
    const clamp = (value: number, min: number, max: number) =>
      Math.min(max, Math.max(min, value));

    const r = Math.round(clamp(color.r, 0, 1) * 255);
    const g = Math.round(clamp(color.g, 0, 1) * 255);
    const b = Math.round(clamp(color.b, 0, 1) * 255);
    const a = clamp(color.a, 0, 1);

    return `rgba(${r}, ${g}, ${b}, ${a})`;
  };

  const markerColorStyle = useMemo(() => {
    if (!markerBorderColor && !markerBackgroundColor && !markerIconColor)
      return undefined;

    return {
      ...(markerBackgroundColor
        ? { backgroundColor: toCssRgba(markerBackgroundColor), color: "white" }
        : {}),
      ...(markerBorderColor ? { borderColor: toCssRgba(markerBorderColor) } : {}),
      ...(markerIconColor ? { color: toCssRgba(markerIconColor) } : {}),
    };
  }, [markerBorderColor, markerBackgroundColor, markerIconColor]);

  const directionTriangleStyle = useMemo(() => {
    if (directionDegrees === undefined || Number.isNaN(directionDegrees))
      return undefined;

    const resolvedColor = directionColor
      ? toCssRgba(directionColor)
      : markerBackgroundColor
      ? toCssRgba(markerBackgroundColor)
      : markerBorderColor
      ? toCssRgba(markerBorderColor)
      : markerIconColor
      ? toCssRgba(markerIconColor)
      : "var(--pi-text-color-1)";

    return {
      "--direction-rotation": `${directionDegrees}deg`,
      "--direction-color": resolvedColor,
    } as React.CSSProperties;
  }, [
    directionDegrees,
    directionColor,
    markerBackgroundColor,
    markerBorderColor,
    markerIconColor,
  ]);

  const isCameraMarker =
    strategy === CameraPointStrategy || strategy === SelectedCameraPointStrategy;

  const icon = new L.DivIcon({
    // Lets camera picking keep cameras prominent while it dims everything else (Viewport3DCameraPickingFocus).
    className: [isCameraMarker ? CAMERA_MARKER_CLASS : "", iconClassName ?? ""].join(" ").trim(),
    html: ReactDOMServer.renderToString(
      <div className="interactionpointpreview-flight">
        <div
          className={
            "interactionpointpreview-node-icon " +
            strategy.getClassnames(disabled)
          }
          style={markerColorStyle}
        >
          {strategy.getView(imageUrl, actor)}
          {directionTriangleStyle && (
            <div
              className="interactionpointpreview-node-direction"
              style={directionTriangleStyle}
            />
          )}
          {showCheckBadge && (
            <div className="interactionpointpreview-node-check-badge">
              <CheckIcon fontSize="inherit" color="inherit" />
            </div>
          )}
          {badgeContent !== undefined && badgeContent !== null && (
            <div
              className={classNames("interactionpointpreview-node-badge", props.badgeClassName)}
              style={
                props.badgeColor
                  ? ({
                      color: toCssRgba(props.badgeColor),
                      "--badge-color": toCssRgba(props.badgeColor),
                    } as React.CSSProperties)
                  : undefined
              }
            >
              {badgeContent}
            </div>
          )}
          {footerContent && (
            <div className="interactionpointpreview-node-footer">
              {footerContent}
            </div>
          )}
        </div>
        </div>
    ),
  });

  // The DivIcon has Leaflet's default 12x12 anchor, so the marker's visual
  // centre sits 6px up-left of the latlng; the 36px node then ends 12px below it.
  // Above the marker: its top edge sits 24px over the latlng, plus room for the arrow.
  const tooltipOffset: [number, number] =
    tooltipDirection === "top" ? [-6, -34] : [-6, 20];

  const onPopoverClick = () => {
    if (popupRef.current) {
      popupRef.current.close();
    }
  };

  return (
    <Marker
      ref={markerRef}
      position={latLang}
      icon={icon}
      zIndexOffset={
        isCameraMarker
          ? (zIndexOffset ?? 0) + CAMERA_MARKER_Z_INDEX_OFFSET
          : zIndexOffset
      }
      title={props.title}
      eventHandlers={{ click: (e) => onClick?.(e.originalEvent) }}
    >
      {popoverContent && (
        <Popup ref={popupRef} closeButton={false} closeOnClick autoClose>
          <div onClick={onPopoverClick}>{popoverContent}</div>
        </Popup>
      )}
      {tooltipContent && !disabled && (
        <Tooltip
          direction={tooltipDirection}
          offset={tooltipOffset}
          opacity={1}
          className={tooltipClassName}
        >
          {tooltipContent}
        </Tooltip>
      )}
    </Marker>
  );
});

export default InteractionPointPreview;
